import { Component, ElementRef, EventEmitter, forwardRef, Input, Output, ViewChild } from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DatePicker } from '@ionic-native/date-picker';
import { Platform } from 'ionic-angular';
import { isDefined, isString } from 'ionic-angular/util/util';
import moment from 'moment';
import { BsDatepickerConfig, BsDatepickerDirective } from 'ngx-bootstrap/datepicker';
import { isDate } from 'util';
import { dateToString } from '../../../../core/util/date-to-string';
import { ControlComponent } from '../../control.component';
import { DateTimeControl } from '../date-time-control.model';

@Component({
    selector: 'vas-datepicker',
    template: `
        <div class="col-xs-12 col-12 col-md-4 form-group">
            <input
                type="hidden"
                #ds="bsDatepicker"
                placement="center"
                [bsValue]="datePickerValue"
                [bsConfig]="{ dateInputFormat: displayFormat }"
                (onHidden)="onHideDatePickerDesktop()"
                bsDatepicker>
        </div>
        <div class="datetime-text datetime-placeholder" [ngClass]="'datepicker'">{{ displayValue }}</div>
        <button
            aria-haspopup="true"
            (click)="showDatePicker()"
            type="button"
            [id]="id"
            ion-button="item-cover"
            class="item-cover showDatePicker">
        </button>
    `,
    providers: [
        {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DateTimeControlComponent), multi: true},
        {provide: NG_VALIDATORS, useExisting: forwardRef(() => DateTimeControlComponent), multi: true}
    ]
})
export class DateTimeControlComponent extends ControlComponent {
    @Input('value') _value = undefined;
    @Input() definition: DateTimeControl;
    @Input() displayFormat: string = 'DD-MM-YYYY'; // see https://date-fns.org/docs/format
    @Output() changes: EventEmitter<any> = new EventEmitter();

    @ViewChild(BsDatepickerDirective) datePicker: BsDatepickerDirective;
    datePickerConfig: Partial<BsDatepickerConfig>;
    datePickerValue: Date;

    constructor(
        protected elemRef: ElementRef,
        protected platform: Platform,
        protected datePickerNative: DatePicker
    ) {
        super(elemRef);

        this.datePickerConfig = {
            dateInputFormat: this.displayFormat
        };
    }

    ngAfterViewInit(): void {
        this.datePicker.outsideClick = true;
    }

    get id(): string {
        return this.definition ? this.definition.name : '';
    }

    /**
     * Returns the field's value formatted for display to the user
     * @return {any}
     */
    get displayValue(): string {
        if (!this.datePickerValue) {
            return '';
        }
        return this.formatDateForOutput(this.datePickerValue);
    }

    /**
     * Formats the a date for display to the user
     * @param value
     * @return {any}
     */
    protected formatDateForOutput(value: Date): string {
        return moment(value).format(this.displayFormat);
    }

    /**
     * Returns true if we are running on a desktop desktop browser
     * @return {boolean}
     */
    protected isDesktop(): boolean {
        return this.platform.is('core');
    }

    /**
     * Shows the date picker
     */
    showDatePicker(): void {
        if (this.isDesktop()) {
            this.showDatePickerDesktop();
        } else {
            this.showDatePickerNative();
        }
    }

    /**
     * Shows the desktop datepicker
     */
    protected showDatePickerDesktop(): void {
        this.datePicker.show();
    }

    /**
     * Shows the mobile native datepicker
     */
    protected showDatePickerNative(): void {

        this.datePickerNative.show({
            date: new Date(),
            mode: 'date',
            androidTheme: this.datePickerNative.ANDROID_THEMES.THEME_HOLO_DARK
        }).then(
            date => this.updateValue(dateToString(date)),
            error => {
                // @todo
                console.error('error picking date', error);
            }
        );
    }

    /**
     * Set the initial value
     * @param value
     */
    writeValue(value: any): void {
        super.writeValue(value);
        if (value) {
            this.datePickerValue = new Date(value);
        }
    }

    updateValue(value: any): void {
        if (value) {
            switch (true) {
                case isDate(value): {
                    this.datePickerValue = value;
                    super.updateValue(dateToString(value));
                    break;
                }
                case isString(value): {
                    this.datePickerValue = new Date(value);
                    super.updateValue(value);
                    break;
                }
                default: {
                    throw new Error('value is not an instance of date or string');
                }
            }
        }

    }

    /**
     * Handles the hide date picker event - when the user picks a date on the desktop datepicker
     */
    onHideDatePickerDesktop(): void {
        const date = this.datePicker._bsValue;
        if (isDefined(date) && date !== this.datePickerValue) {
            this.updateValue(date);
        }
    }
}



// WEBPACK FOOTER //
// ./src/vas/controls/datetime/form/date-time-control.component.ts